import { useRef } from "react";
import { X, Image as ImageIcon, Circle, Ban } from "lucide-react";
import { toast } from "sonner";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { BACKGROUND_PRESETS } from "@/lib/backgroundPresets";

// Default test-ids = the existing Meeting Room set (do not change — QA relies
// on them). The Lobby passes its own `ids` set via the prop.
const DEFAULT_IDS = {
  panel: "video-effects-panel",
  close: "close-video-effects",
  bgNone: "effect-bg-none",
  bgBlur: "effect-bg-blur",
  bgImage: "effect-bg-image",
  blurLow: "effect-blur-low",
  blurMedium: "effect-blur-medium",
  blurHigh: "effect-blur-high",
  presets: "effect-bg-presets",
  presetPrefix: "effect-preset-",
  upload: "effect-bg-upload",
  fileInput: "effect-bg-file-input",
  beauty: "effect-beauty-toggle",
  sliders: "effect-beauty-sliders",
  smoothness: "effect-smoothness",
  brightness: "effect-brightness",
  redness: "effect-redness",
};

// Downscale an uploaded image to a safe size and return a compact JPEG data
// URL (keeps localStorage well under quota and avoids giant textures).
async function fileToDataUrl(file) {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise((res, rej) => {
      const i = new Image();
      i.onload = () => res(i);
      i.onerror = rej;
      i.src = url;
    });
    const maxW = 1920;
    const maxH = 1080;
    let { width, height } = img;
    const scale = Math.min(1, maxW / width, maxH / height);
    width = Math.round(width * scale);
    height = Math.round(height * scale);
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    canvas.getContext("2d").drawImage(img, 0, 0, width, height);
    return canvas.toDataURL("image/jpeg", 0.85);
  } finally {
    URL.revokeObjectURL(url);
  }
}

export default function VideoEffectsPanel({ settings, onChange, onClose, ids, caps }) {
  const fileRef = useRef(null);
  const s = settings || {};
  const T = { ...DEFAULT_IDS, ...(ids || {}) };
  const c = caps || { backgroundSupported: true, beautySupported: true };

  const chip = (active) =>
    "px-2 py-2 text-xs text-center border transition-colors " +
    (active
      ? "bg-[#005e7a] border-[#0a7ea4] text-white"
      : "bg-white/5 border-white/10 text-white/85 hover:bg-white/10");

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file.");
      return;
    }
    try {
      const dataUrl = await fileToDataUrl(file);
      onChange({ background: "image", imageDataUrl: dataUrl, presetId: null });
    } catch {
      toast.error("Couldn't load that image.");
    }
  };

  const blurLevels = [
    { key: 1, label: "Low", id: T.blurLow },
    { key: 2, label: "Medium", id: T.blurMedium },
    { key: 3, label: "High", id: T.blurHigh },
  ];

  return (
    <div
      className="fixed left-1/2 -translate-x-1/2 bottom-24 z-50 w-[340px] max-w-[92vw] p-4 text-white"
      style={{
        background: "rgba(20,20,26,0.96)",
        backdropFilter: "blur(24px)",
        border: "1px solid rgba(255,255,255,0.1)",
      }}
      data-testid={T.panel}
    >
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm font-semibold tracking-wide">Video effects</p>
        <button
          onClick={onClose}
          className="text-white/60 hover:text-white"
          data-testid={T.close}
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Background */}
      {c.backgroundSupported && (
        <>
          <p className="text-[11px] uppercase tracking-widest text-white/50 mb-2">
            Background
          </p>
          <div className="grid grid-cols-3 gap-2 mb-3">
            <button
              onClick={() => onChange({ background: "none" })}
              className={chip(s.background === "none")}
              data-testid={T.bgNone}
            >
              <Ban className="w-4 h-4 mx-auto mb-1" /> None
            </button>
            <button
              onClick={() => onChange({ background: "blur" })}
              className={chip(s.background === "blur")}
              data-testid={T.bgBlur}
            >
              <Circle className="w-4 h-4 mx-auto mb-1" /> Blur
            </button>
            <button
              onClick={() => onChange({ background: "image" })}
              className={chip(s.background === "image")}
              data-testid={T.bgImage}
            >
              <ImageIcon className="w-4 h-4 mx-auto mb-1" /> Image
            </button>
          </div>

          {s.background === "blur" && (
            <div className="grid grid-cols-3 gap-2 mb-3">
              {blurLevels.map((b) => (
                <button
                  key={b.key}
                  onClick={() => onChange({ blurDegree: b.key })}
                  className={chip(s.blurDegree === b.key)}
                  data-testid={b.id}
                >
                  {b.label}
                </button>
              ))}
            </div>
          )}

          {s.background === "image" && (
            <div className="mb-3">
              <div className="grid grid-cols-3 gap-2 mb-2" data-testid={T.presets}>
                {BACKGROUND_PRESETS.map((p) => (
                  <button
                    key={p.id}
                    onClick={() =>
                      onChange({ background: "image", imageDataUrl: p.src, presetId: p.id })
                    }
                    className={
                      "relative h-14 overflow-hidden border transition-colors " +
                      (s.presetId === p.id
                        ? "border-[#0a7ea4] ring-2 ring-[#0a7ea4]"
                        : "border-white/10 hover:border-white/40")
                    }
                    title={p.label}
                    data-testid={`${T.presetPrefix}${p.id}`}
                  >
                    <img src={p.src} alt={p.label} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
              {s.imageDataUrl && !s.presetId && (
                <img
                  src={s.imageDataUrl}
                  alt="Custom background"
                  className="w-full h-20 object-cover mb-2"
                  style={{ border: "1px solid rgba(255,255,255,0.12)" }}
                />
              )}
              <button
                onClick={() => fileRef.current?.click()}
                className={chip(!!s.imageDataUrl && !s.presetId) + " w-full"}
                data-testid={T.upload}
              >
                {s.imageDataUrl && !s.presetId ? "Replace custom image" : "Upload your own"}
              </button>
            </div>
          )}
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleUpload}
            data-testid={T.fileInput}
          />
        </>
      )}

      {c.backgroundSupported && c.beautySupported && (
        <div className="h-px bg-white/10 my-3" />
      )}

      {/* Beauty */}
      {c.beautySupported && (
        <>
          <div className="flex items-center justify-between mb-3">
            <p className="text-[11px] uppercase tracking-widest text-white/50">
              Touch up my appearance
            </p>
            <Switch
              checked={!!s.beautyEnabled}
              onCheckedChange={(v) => onChange({ beautyEnabled: v })}
              data-testid={T.beauty}
            />
          </div>

          {s.beautyEnabled && (
            <div className="space-y-3" data-testid={T.sliders}>
              <SliderRow
                label="Smoothness"
                value={s.smoothnessLevel}
                onChange={(v) => onChange({ smoothnessLevel: v })}
                testid={T.smoothness}
              />
              <SliderRow
                label="Brightness"
                value={s.lighteningLevel}
                onChange={(v) => onChange({ lighteningLevel: v })}
                testid={T.brightness}
              />
              <SliderRow
                label="Redness"
                value={s.rednessLevel}
                onChange={(v) => onChange({ rednessLevel: v })}
                testid={T.redness}
              />
            </div>
          )}
        </>
      )}

      <p className="text-[11px] text-white/40 mt-3 leading-snug">
        Effects apply to your video only. Best on desktop Chrome.
      </p>
    </div>
  );
}

function SliderRow({ label, value, onChange, testid }) {
  return (
    <div>
      <div className="flex justify-between text-xs text-white/70 mb-1">
        <span>{label}</span>
        <span>{Math.round((value || 0) * 100)}%</span>
      </div>
      <Slider
        min={0}
        max={1}
        step={0.05}
        value={[value || 0]}
        onValueChange={(arr) => onChange(arr[0])}
        data-testid={testid}
      />
    </div>
  );
}
